/**
 * Reports Hooks
 * React Query hooks for reports dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/lib/api/reports';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

// Query keys
const REPORTS_KEYS = {
  all: ['reports'] as const,
  dashboard: (startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) =>
    [...REPORTS_KEYS.all, 'dashboard', { startDate, endDate, excludeTeam }] as const,
  users: (startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) =>
    [...REPORTS_KEYS.all, 'users', { startDate, endDate, excludeTeam }] as const,
  daily: (startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) =>
    [...REPORTS_KEYS.all, 'daily', { startDate, endDate, excludeTeam }] as const,
  revenue: (startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) =>
    [...REPORTS_KEYS.all, 'revenue', { startDate, endDate, excludeTeam }] as const,
  pnl: (startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) =>
    [...REPORTS_KEYS.all, 'pnl', { startDate, endDate, excludeTeam }] as const,
};

export interface UseReportsDashboardOptions {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
  refetchInterval?: number;
  enabled?: boolean;
}

/**
 * Hook for fetching complete reports dashboard
 */
export function useReportsDashboard(options: UseReportsDashboardOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, refetchInterval, enabled = true } = options;

  return useQuery({
    queryKey: REPORTS_KEYS.dashboard(startDate, endDate, excludeTeam),
    queryFn: () => reportsApi.getDashboard(startDate, endDate, excludeTeam),
    refetchInterval,
    enabled,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for fetching user statistics
 */
export function useUserStats(startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) {
  return useQuery({
    queryKey: REPORTS_KEYS.users(startDate, endDate, excludeTeam),
    queryFn: () => reportsApi.getUserStats(startDate, endDate, excludeTeam),
    staleTime: 30000,
  });
}

/**
 * Hook for fetching daily statistics
 */
export function useDailyStats(startDate?: string, endDate?: string, excludeTeam = DEFAULT_EXCLUDE_TEAM) {
  return useQuery({
    queryKey: REPORTS_KEYS.daily(startDate, endDate, excludeTeam),
    queryFn: () => reportsApi.getDailyStats(startDate, endDate, excludeTeam),
    staleTime: 30000,
  });
}

export function useRevenueAnalytics(
  options: { startDate?: string; endDate?: string; excludeTeam?: boolean; enabled?: boolean } = {},
) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery({
    queryKey: REPORTS_KEYS.revenue(startDate, endDate, excludeTeam),
    queryFn: () => reportsApi.getRevenueAnalytics(startDate, endDate, excludeTeam),
    enabled,
    staleTime: 30000,
  });
}

export function usePnlSummary(
  options: { startDate?: string; endDate?: string; excludeTeam?: boolean; enabled?: boolean } = {},
) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery({
    queryKey: REPORTS_KEYS.pnl(startDate, endDate, excludeTeam),
    queryFn: () => reportsApi.getPnlSummary(startDate, endDate, excludeTeam),
    enabled,
    staleTime: 30000,
  });
}

// Re-export types
export type { ReportsDashboard, CostConfig } from '@/lib/api/reports';
